import React, { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Plus, Search } from 'lucide-react'
import {
  Button,
  Input,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Badge,
  Switch,
  Spinner,
} from '@wove/web-ui'
import { ValidationRulesTable } from './ValidationRulesTable'
import { ValidationRuleFormDialog } from './ValidationRuleFormDialog'
import { ConfirmationDialog } from './ConfirmationDialog'
import { 
  getValidationRules, 
  deleteValidationRule as deleteValidationRuleApi,
  duplicateValidationRule,
  reorderValidationRules,
  type ValidationRule 
} from '@wove/api'
import { toast } from 'sonner'

export default function ValidationRulesPage() {
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all')
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false)
  const [editingRule, setEditingRule] = useState<ValidationRule | null>(null)
  const [duplicateConfirm, setDuplicateConfirm] = useState<{
    open: boolean
    rule?: ValidationRule
  }>({ open: false })
  const [deleteConfirm, setDeleteConfirm] = useState<{ open: boolean; id?: string; name?: string }>(
    { open: false },
  )

  const queryClient = useQueryClient()

  const {
    data: rulesResponse,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['validation-rules', search, statusFilter],
    queryFn: () =>
      getValidationRules({
        search,
        status: statusFilter === 'all' ? undefined : statusFilter,
      }),
  })

  const rules = rulesResponse?.rules

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteValidationRuleApi(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['validation-rules'] })
      toast.success('Validation rule deleted successfully')
    },
    onError: () => {
      toast.error('Failed to delete validation rule')
    },
  })

  const reorderMutation = useMutation({
    mutationFn: (ruleIds: string[]) => reorderValidationRules(ruleIds),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['validation-rules'] })
      toast.success('Rules reordered successfully')
    },
    onError: () => {
      toast.error('Failed to reorder rules')
    },
  })

  const handleDelete = (id: string) => {
    const rule = rules?.find((r) => r.id === id)
    setDeleteConfirm({ open: true, id, name: rule?.name })
  }

  const handleEdit = (rule: ValidationRule) => {
    setEditingRule(rule)
  }

  const handleDuplicate = (rule: ValidationRule) => {
    setDuplicateConfirm({ open: true, rule })
  }

  const confirmDuplicate = async () => {
    if (duplicateConfirm.rule) {
      try {
        await duplicateValidationRule(duplicateConfirm.rule.id, `${duplicateConfirm.rule.name} (Copy)`)
        queryClient.invalidateQueries({ queryKey: ['validation-rules'] })
        toast.success('Validation rule duplicated successfully')
        setDuplicateConfirm({ open: false })
      } catch (error) {
        toast.error('Failed to duplicate validation rule')
      }
    }
  }

  const confirmDelete = () => {
    if (deleteConfirm.id) {
      deleteMutation.mutate(deleteConfirm.id)
    }
  }

  const handleReorder = (newOrder: ValidationRule[]) => {
    const ruleIds = newOrder.map((rule) => rule.id)
    reorderMutation.mutate(ruleIds)
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-96">
        <p className="text-red-500 mb-4">Failed to load validation rules</p>
        <Button onClick={() => window.location.reload()}>Retry</Button>
      </div>
    )
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <h2 className="text-2xl font-bold mb-2">Validation Rules</h2>
        <p className="text-gray-600">
          Configure rules to dynamically control which fields are required based on shipment
          characteristics
        </p>
      </div>

      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              type="text"
              placeholder="Search rules..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 w-80"
            />
          </div>

          <div className="flex items-center gap-2">
            <Button
              variant={statusFilter === 'all' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setStatusFilter('all')}
            >
              All
            </Button>
            <Button
              variant={statusFilter === 'active' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setStatusFilter('active')}
            >
              Active
            </Button>
            <Button
              variant={statusFilter === 'inactive' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setStatusFilter('inactive')}
            >
              Inactive
            </Button>
          </div>
        </div>

        <Button onClick={() => setIsCreateDialogOpen(true)}>
          <Plus className="h-4 w-4 mr-2" />
          Add Rule
        </Button>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-12">
          <Spinner />
        </div>
      ) : (
        <ValidationRulesTable
          rules={rules || []}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onDuplicate={handleDuplicate}
          onReorder={handleReorder}
        />
      )}

      <ValidationRuleFormDialog
        open={isCreateDialogOpen || !!editingRule}
        onOpenChange={(open) => {
          if (!open) {
            setIsCreateDialogOpen(false)
            setEditingRule(null)
          }
        }}
        rule={editingRule}
        onSuccess={() => {
          queryClient.invalidateQueries({ queryKey: ['validation-rules'] })
          setIsCreateDialogOpen(false)
          setEditingRule(null)
        }}
      />

      <ConfirmationDialog
        open={duplicateConfirm.open}
        onOpenChange={(open) => setDuplicateConfirm({ open })}
        title="Duplicate Validation Rule"
        description={`Are you sure you want to duplicate "${duplicateConfirm.rule?.name}"? The duplicate will be created as inactive.`}
        confirmText="Duplicate"
        onConfirm={confirmDuplicate}
      />

      <ConfirmationDialog
        open={deleteConfirm.open}
        onOpenChange={(open) => setDeleteConfirm({ open })}
        title="Delete Validation Rule"
        description={`Are you sure you want to delete "${deleteConfirm.name}"? This action cannot be undone.`}
        confirmText="Delete"
        onConfirm={confirmDelete}
        variant="destructive"
      />
    </div>
  )
}
